import { X, Loader2, CheckCircle2, AlertCircle, Info } from "lucide-react";
import usePaymentRegister from "./payment-register/usePaymentRegister";
import PaymentPositionStats from "./payment-register/PaymentPositionStats";
import PaymentForm from "./payment-register/PaymentForm";
import PaymentHistory from "./payment-register/PaymentHistory";

function ModalHeader({ client, onClose }) {
  return (
    <header className="flex items-center justify-between px-5 py-3 border-b border-[#E6E1D6] bg-[#FAF8F2]">
      <div>
        <h2 className="text-base font-bold uppercase tracking-[0.12em] text-[#0F1829]">
          Registar Pagamento de Comissão
        </h2>
        <p className="text-xs text-[#6B7280] mt-0.5">
          <span className="font-mono text-[#978D78]">#{client?.ID_Cliente}</span>{" "}
          <span className="font-medium text-[#0F1829]">{client?.NomeCompleto}</span>
          {client?.Email && <span className="text-[#6B7280]"> · {client.Email}</span>}
        </p>
      </div>
      <button onClick={onClose}
        className="p-1.5 rounded hover:bg-white border border-transparent hover:border-[#E6E1D6]"
        data-testid="register-payment-close">
        <X size={16} />
      </button>
    </header>
  );
}

function ImmutabilityNotice() {
  return (
    <div className="flex items-start gap-2 px-3 py-2 bg-[#FAF8F2] border border-[#E6E1D6] rounded text-xs text-[#4B5563]">
      <Info size={13} className="mt-0.5 shrink-0 text-[#978D78]" />
      <span>
        Pagamentos registados são imutáveis. Para corrigir, regista um novo pagamento com{" "}
        <strong>valor negativo</strong>.
      </span>
    </div>
  );
}

function FeedbackBanners({ err, ok }) {
  return (
    <>
      {err && (
        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded"
          data-testid="register-payment-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {ok && (
        <div className="flex items-start gap-2 px-3 py-2 bg-green-50 border border-green-200 text-sm text-green-700 rounded"
          data-testid="register-payment-success">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> {ok}
        </div>
      )}
    </>
  );
}

function ModalFooter({ onClose, onSubmit, submitting }) {
  return (
    <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E6E1D6] bg-[#FAF8F2]">
      <button onClick={onClose}
        className="px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-white"
        data-testid="register-payment-cancel">
        Fechar
      </button>
      <button onClick={onSubmit} disabled={submitting}
        className="px-4 py-2 text-sm bg-[#0F1829] text-white rounded hover:bg-black disabled:opacity-50 flex items-center gap-1.5"
        data-testid="register-payment-submit">
        {submitting && <Loader2 size={14} className="animate-spin" />}
        Registar Pagamento
      </button>
    </footer>
  );
}

export default function RegisterCommissionPaymentModal({ client, onClose, onRegistered }) {
  const m = usePaymentRegister(client, onRegistered);
  return (
    <div role="dialog" aria-modal="true"
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="register-payment-modal">
      <div className="bg-white w-full max-w-2xl rounded-lg border border-[#E6E1D6] shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <ModalHeader client={client} onClose={onClose} />

        <section className="px-5 py-4 space-y-4">
          <PaymentPositionStats position={m.position} fallback={client} />
          <ImmutabilityNotice />
          <PaymentForm
            data={m.data} setData={m.setData}
            valor={m.valor} setValor={m.setValor}
            tipo={m.tipo} setTipo={m.setTipo}
            obs={m.obs} setObs={m.setObs}
            types={m.types}
          />
          <FeedbackBanners err={m.err} ok={m.ok} />
          <PaymentHistory payments={m.payments} />
        </section>

        <ModalFooter onClose={onClose} onSubmit={m.submit} submitting={m.submitting} />
      </div>
    </div>
  );
}
